import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Briefcase, Hammer, User, Wrench, Shield, CheckCircle, Download, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

// PWA Download Button Component
const DownloadAppButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isInStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                           window.navigator.standalone === true;
    setIsStandalone(isInStandalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleDownloadClick = async () => {
    if (isStandalone) {
      toast.success('App is already installed on your device!');
      return;
    }

    if (isIOS) {
      // Show iOS instructions
      toast.info(
        <div className="space-y-2">
          <p className="font-semibold">Add to Home Screen:</p>
          <ol className="list-decimal list-inside space-y-1 text-sm">
            <li>Tap the Share button (📤) at the bottom</li>
            <li>Scroll and tap "Add to Home Screen"</li>
            <li>Tap "Add" to confirm</li>
          </ol>
        </div>,
        { duration: 12000 }
      );
      return;
    }

    if (!deferredPrompt) {
      console.log('[Landing] No deferred prompt available - browser may not support PWA install');
      
      // Show helpful message for browsers without native install prompt
      toast.info(
        <div className="space-y-2">
          <p className="font-semibold">Install Bohol Trabaho</p>
          <p className="text-sm">
            Use your browser's menu to add this app to your home screen:
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            <strong>Chrome/Edge:</strong> Menu (⋮) → "Install app"<br/>
            <strong>Samsung Internet:</strong> Menu → "Add to Home screen"<br/>
            <strong>Firefox:</strong> Menu → "Install"
          </p>
        </div>,
        { duration: 15000 }
      );
      return;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        toast.success('Bohol Trabaho app installed successfully!');
      }
      
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error installing app:', error);
      toast.error('Failed to install. Try using your browser menu.');
    }
  };

  return (
    <Button
      size="lg"
      onClick={handleDownloadClick}
      className="bg-primary hover:bg-primary/90 text-white rounded-xl px-8 py-6 text-lg font-bold shadow-2xl"
      data-testid="download-app-btn"
    >
      <Download className="mr-2" size={24} />
      {isStandalone ? 'App Already Installed' : 'Download App Now'}
    </Button>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [logoUrl, setLogoUrl] = useState('/logo-transparent.png');
  const [appVersion, setAppVersion] = useState('3.1.1');

  useEffect(() => {
    // Fetch custom logo and app version from database
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('setting_value, setting_key')
          .in('setting_key', ['app_logo_url', 'app_version']);
        
        if (data) {
          data.forEach(setting => {
            if (setting.setting_key === 'app_logo_url' && setting.setting_value?.url) {
              setLogoUrl(setting.setting_value.url);
            }
            if (setting.setting_key === 'app_version') {
              setAppVersion(setting.setting_value);
            }
          });
        }
      } catch (error) {
        console.log('Using default settings');
      }
    };
    
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen-safe">
      {/* Navigation Bar */}
      <nav className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/50 to-transparent">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="group inline-flex items-center gap-2"
            aria-label="Go to home"
            title="Bohol Trabaho"
            data-testid="landing-site-logo"
          >
            <img
              src={logoUrl}
              alt="Bohol Trabaho logo"
              className="h-10 md:h-12 w-auto transition-transform duration-200 ease-out group-hover:scale-105 active:scale-95 drop-shadow-lg"
              onError={(e)=>{ e.currentTarget.onerror=null; e.currentTarget.src='/logo-transparent.png'; }}
            />
          </button>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/auth?mode=login')}
              className="text-white hover:bg-white/20 rounded-xl"
            >
              Log In
            </Button>
            <Button
              onClick={() => navigate('/auth?mode=signup')}
              className="bg-white hover:bg-gray-100 text-primary rounded-xl font-semibold"
            >
              Register
            </Button>
          </div>
        </div>
      </nav>
      
      {/* Hero Section with Bohol Background */}
      <section
        className="relative px-4 py-20 md:py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1708353234006-5f1a3f92a63a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxDaG9jb2xhdGUlMjBIaWxscyUyMEJvaG9sfGVufDB8fHx8MTc2Mzg1NjIwMXww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/50" />
        
        <div className="relative max-w-5xl mx-auto text-center text-white">
          <div className="flex items-center justify-center mb-6">
            <img
              src={logoUrl}
              alt="Bohol Trabaho logo"
              className="h-16 md:h-24 lg:h-28 w-auto mx-auto drop-shadow-[0_6px_30px_rgba(0,0,0,0.4)] transition-transform duration-200 ease-out hover:scale-[1.02] active:scale-95"
              data-testid="hero-logo"
              onError={(e)=>{ e.currentTarget.onerror=null; e.currentTarget.src='/logo-transparent.png'; }}
            />
          </div>
          <h1 className="font-heading font-bold text-4xl md:text-6xl lg:text-7xl mb-6 drop-shadow-2xl">
            {t('landing.headline')}
          </h1>
          <p className="text-lg md:text-2xl mb-8 max-w-3xl mx-auto drop-shadow-lg font-medium">
            {t('landing.subheadline')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/auth?mode=signup')}
              className="bg-white hover:bg-gray-100 text-primary rounded-xl px-8 py-6 text-lg font-bold shadow-2xl hover:shadow-[0_8px_30px_rgba(255,255,255,0.3)] transform hover:-translate-y-0.5 transition-all duration-200"
              data-testid="get-started-btn"
            >
              {t('auth.signUp')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/auth?mode=login')}
              className="border-2 border-white text-white hover:bg-white/20 rounded-xl px-8 py-6 text-lg font-bold backdrop-blur-sm transform hover:-translate-y-0.5 transition-all duration-200"
            >
              {t('auth.signIn')}
            </Button>
          </div>
        </div>
      </section>

      {/* 4 Cards Section */}
      <section className="px-4 py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Card 1 - Hiring Manager (Employer) */}
            <Card className="card-enhanced hover:shadow-2xl transition-all duration-300 border-2 hover:border-primary transform hover:-translate-y-1">
              <CardContent className="p-6 sm:p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center mb-6">
                  <Briefcase className="text-primary" size={32} />
                </div>
                <h3 className="heading-primary text-2xl mb-3">Hiring Manager</h3>
                <p className="text-muted-foreground mb-4">Hiring full-time or long-term staff?</p>
                <div className="mb-6">
                  <p className="text-sm text-muted-foreground mb-2">Perfect for:</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">Receptionist</Badge>
                    <Badge variant="secondary">Waiter</Badge>
                    <Badge variant="secondary">Nurse</Badge>
                    <Badge variant="secondary">Office Admin</Badge>
                    <Badge variant="secondary">Security</Badge>
                  </div>
                </div>
                <Button
                  className="btn-primary-enhanced w-full rounded-xl text-base py-6"
                  onClick={() => navigate('/auth?mode=signup&role=employer')}
                  data-testid="employer-signup-btn"
                >
                  I'm Hiring Permanent Staff →
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-3">
                  <strong className="text-green-600">100% FREE</strong> · Post jobs and view applicants at no cost
                </p>
              </CardContent>
            </Card>

            {/* Card 2 - Project Owner */}
            <Card className="card-enhanced hover:shadow-2xl transition-all duration-300 border-2 hover:border-primary transform hover:-translate-y-1">
              <CardContent className="p-6 sm:p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-accent/20 to-accent/10 rounded-full flex items-center justify-center mb-6">
                  <Hammer className="text-accent" size={32} />
                </div>
                <h3 className="heading-primary text-2xl mb-3">Project Owner</h3>
                <p className="text-muted-foreground mb-4">Need someone for a project or quick job?</p>
                <div className="mb-6">
                  <p className="text-sm text-muted-foreground mb-2">Perfect for:</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">Renovation</Badge>
                    <Badge variant="secondary">Plumbing</Badge>
                    <Badge variant="secondary">Website</Badge>
                    <Badge variant="secondary">Photography</Badge>
                    <Badge variant="secondary">Boat Repair</Badge>
                  </div>
                </div>
                <Button
                  className="btn-accent-enhanced w-full rounded-xl text-base py-6 text-white"
                  onClick={() => navigate('/auth?mode=signup&role=owner')}
                  data-testid="owner-signup-btn"
                >
                  I Have a Project or Gig →
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-3">
                  <strong className="text-green-600">100% FREE</strong> · Post gigs and view bidders at no cost
                </p>
              </CardContent>
            </Card>

            {/* Card 3 - Job Seeker */}
            <Card className="card-enhanced hover:shadow-2xl transition-all duration-300 border-2 hover:border-primary transform hover:-translate-y-1">
              <CardContent className="p-6 sm:p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center mb-6">
                  <User className="text-primary" size={32} />
                </div>
                <h3 className="heading-primary text-2xl mb-3">Job Seeker</h3>
                <p className="text-muted-foreground mb-4">Looking for stable, long-term work?</p>
                <div className="mb-6">
                  <p className="text-sm text-muted-foreground mb-2">Perfect for:</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">Monthly Salary</Badge>
                    <Badge variant="secondary">Benefits</Badge>
                    <Badge variant="secondary">Career Growth</Badge>
                  </div>
                </div>
                <Button
                  className="btn-primary-enhanced w-full rounded-xl text-base py-6"
                  onClick={() => navigate('/auth?mode=signup&role=job_seeker')}
                  data-testid="seeker-signup-btn"
                >
                  I'm Looking for Full-Time Work →
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-3">
                  <strong className="text-green-600">100% FREE forever</strong> · Get verified for more trust
                </p>
              </CardContent>
            </Card>

            {/* Card 4 - Contractor/Freelancer */}
            <Card className="card-enhanced hover:shadow-2xl transition-all duration-300 border-2 hover:border-accent transform hover:-translate-y-1">
              <CardContent className="p-6 sm:p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-accent/20 to-accent/10 rounded-full flex items-center justify-center mb-6">
                  <Wrench className="text-accent" size={32} />
                </div>
                <h3 className="heading-primary text-2xl mb-3">Contractor/Freelancer</h3>
                <p className="text-muted-foreground mb-4">Want project-based or freelance work?</p>
                <div className="mb-6">
                  <p className="text-sm text-muted-foreground mb-2">Perfect for:</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">Get Paid Per Job</Badge>
                    <Badge variant="secondary">Set Your Rate</Badge>
                    <Badge variant="secondary">Work When You Want</Badge>
                  </div>
                </div>
                <Button
                  className="btn-accent-enhanced w-full rounded-xl text-base py-6 text-white"
                  onClick={() => navigate('/auth?mode=signup&role=contractor')}
                  data-testid="contractor-signup-btn"
                >
                  I'm a Contractor or Freelancer →
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-3">
                  <strong className="text-green-600">100% FREE forever</strong> · Get verified to appear higher
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* PWA Download Section */}
      <section className="px-4 py-16 bg-gradient-to-br from-primary/10 via-background to-primary/5">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Briefcase className="text-primary" size={48} />
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl mb-4">
            Download Bohol Trabaho App
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Get the app on your phone for quick access, offline support, and faster job hunting. Works like a native app!
          </p>
          <DownloadAppButton />
          <p className="text-sm text-muted-foreground mt-6">
            ✓ Works offline • ✓ Fast & native-like • ✓ No app store needed
          </p>
        </div>
      </section>

      {/* Verification Benefits Section */}
      <section className="px-4 py-16 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="mx-auto mb-6 text-primary" size={64} />
          <h2 className="font-heading font-bold text-3xl md:text-4xl mb-4">
            {t('landing.idVerification')}
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mt-8 text-left">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-semibold mb-1">{t('landing.trueBoholanoBadge')}</h4>
                <p className="text-sm text-muted-foreground">{t('landing.badgeExplanation')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-semibold mb-1">Appear higher in search</h4>
                <p className="text-sm text-muted-foreground">Get priority placement</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-semibold mb-1">Priority support</h4>
                <p className="text-sm text-muted-foreground">Faster help when you need it</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-semibold mb-1">Special "Trusted" filter</h4>
                <p className="text-sm text-muted-foreground">Employers can find verified workers</p>
              </div>
            </div>
          </div>
          <Button
            size="lg"
            variant="outline"
            className="mt-8 rounded-xl text-base px-8 py-6"
            onClick={() => navigate('/auth?mode=signup')}
          >
            Learn More About Verification →
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 py-8 bg-primary text-white text-center">
        <p className="text-lg font-semibold mb-2">{t('footer.tagline')}</p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-3 mb-2">
          <Link to="/privacy" className="text-sm text-white/90 hover:text-white hover:underline" data-testid="footer-privacy-link">
            {t('footer.privacy')}
          </Link>
          <span className="text-white/50">·</span>
          <Link to="/terms" className="text-sm text-white/90 hover:text-white hover:underline" data-testid="footer-terms-link">
            {t('footer.terms', 'Terms of Service')}
          </Link>
          <span className="text-white/50">·</span>
          <Link to="/delete-data" className="text-sm text-white/90 hover:text-white hover:underline" data-testid="footer-delete-link">
            {t('footer.deleteData')}
          </Link>
        </div>
        <p className="text-sm text-white/80">{t('footer.copyright')}</p>
        <p className="text-xs text-white/60 mt-2" data-testid="app-version">{t('footer.version')} {appVersion}</p>
      </footer>
    </div>
  );
};

export default Landing;